import { useEffect, useReducer } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import { csrfHeader } from '../csrf';
import { apiBaseUrl } from '../../lib/env';
import type { components } from '../schema';

export type OwnProfile = components['schemas']['OwnProfile'];
export type PublicProfile = components['schemas']['PublicProfile'];
export type UpdateMeRequest = components['schemas']['UserEndpointsUpdateMeRequest'];

export function useOwnProfile() {
  return useQuery({
    queryKey: ['profile', 'me'],
    queryFn: async () => {
      const { data, error } = await api.GET('/v5/users/me');
      if (error) throw error;
      return data;
    },
  });
}

export function useUpdateOwnProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Partial<UpdateMeRequest>) => {
      const { data, error } = await api.PATCH('/v5/users/me', { body: body as UpdateMeRequest });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['profile'] });
      qc.invalidateQueries({ queryKey: ['session'] });
    },
  });
}

export function useUploadProfilePicture() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);

      // Multipart bypasses openapi-fetch — wire CSRF + cookie auth by hand
      // the same way useUploadPhoto does.
      const headers: Record<string, string> = { 'X-Requested-With': 'haket-v5-ui', ...csrfHeader() };

      const res = await fetch(`${apiBaseUrl}/v5/users/me/profile-picture`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: fd,
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `upload failed: ${res.status}`);
      }
      return (await res.json()) as { url: string };
    },
    onSuccess: () => {
      // Profile-picture URL is stable per-username so React-Query won't
      // refetch it on its own; bump the busterKey instead (see hook below).
      qc.invalidateQueries({ queryKey: ['profile'] });
      qc.invalidateQueries({ queryKey: ['session'] });
      bumpAvatarBuster();
    },
  });
}

export function useDeleteProfilePicture() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error } = await api.DELETE('/v5/users/me/profile-picture');
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['profile'] });
      qc.invalidateQueries({ queryKey: ['session'] });
      bumpAvatarBuster();
    },
  });
}

// Avatar URLs are derived from `username` and never change shape, so we
// can't rely on cache-key invalidation alone to make <img> refetch. We
// bump a module-level counter on every upload/delete and expose it via
// the hook below; the avatar component reads it to append a `?v=…` to
// the URL when the user updates their picture in this tab.
let avatarBumpCounter = 0;
const avatarBumpListeners = new Set<() => void>();

function bumpAvatarBuster() {
  avatarBumpCounter += 1;
  avatarBumpListeners.forEach((l) => l());
}

export function useAvatarCacheBuster() {
  const [, force] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    avatarBumpListeners.add(force);
    return () => { avatarBumpListeners.delete(force); };
  }, []);
  return avatarBumpCounter;
}

export function usePublicProfile(id: string | undefined) {
  return useQuery({
    queryKey: ['profile', 'user', id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await api.GET('/v5/users/{id}', { params: { path: { id: id! } } });
      if (error) throw error;
      return data;
    },
  });
}
